import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { setCookie, hasCookieConsent } from '../utils/cookies';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for remember me token first
    const rememberMe = localStorage.getItem('rememberMe');
    let token = null;
    let userData = null;

    if (rememberMe === 'true') {
      // Use localStorage for remember me users
      token = localStorage.getItem('token');
      const userString = localStorage.getItem('user');
      if (userString) {
        userData = JSON.parse(userString);
      }
    } else {
      // Use sessionStorage for session-only users
      token = sessionStorage.getItem('token');
      const userString = sessionStorage.getItem('user');
      if (userString) {
        userData = JSON.parse(userString);
      }
    }

    if (token && userData) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
    }
    setLoading(false);

    // Add axios interceptor to always use the correct token from either storage
    const interceptor = axios.interceptors.request.use((config) => {
      const rememberMeStatus = localStorage.getItem('rememberMe');
      let currentToken = null;

      if (rememberMeStatus === 'true') {
        currentToken = localStorage.getItem('token');
      } else {
        currentToken = sessionStorage.getItem('token');
      }

      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }

      return config;
    }, (error) => {
      return Promise.reject(error);
    });

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      console.log('AuthContext login: sending request to /api/auth/login');
      const base = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.post(`${base}/auth/login`, { email, password, rememberMe });
      console.log('AuthContext login: response received', response.data);
      if (response.data.mfaRequired) {
        return { success: true, mfaRequired: true, mfaToken: response.data.mfaToken };
      }
      const { token, user: userData } = response.data;

      // Store token based on remember me preference
      if (rememberMe) {
        // Remember for 2 days
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('rememberMe', 'true');
      } else {
        // Session only - clear on browser close
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(userData));
        localStorage.removeItem('rememberMe');
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      // return the user data so callers can make redirect decisions
      return { success: true, user: userData };
    } catch (error) {
      console.error('AuthContext login error:', error);
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  const completeMfaLogin = async (mfaToken, code, rememberMe = false) => {
    try {
      const base = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.post(`${base}/api/auth/login/mfa`, { mfaToken, code, rememberMe });
      const { token, user: userData } = response.data;
      if (rememberMe) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('rememberMe', 'true');
      } else {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(userData));
        localStorage.removeItem('rememberMe');
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'MFA verification failed' };
    }
  };

  const completeDuoLogin = async (handoffId) => {
    try {
      const base = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.post(`${base}/api/auth/login/duo/complete`, { handoffId });
      const { token, user: userData } = response.data;
      const rememberMe = response.data.rememberMe;
      if (rememberMe) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('rememberMe', 'true');
      } else {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(userData));
        localStorage.removeItem('rememberMe');
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Push sign-in could not be completed.' };
    }
  };

  const register = async (userData) => {
    try {
      const base = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.post(`${base}/auth/register`, userData);
      const { token, user: newUser } = response.data;
      // Only set auth cookie if user has consented to cookies
      if (hasCookieConsent()) {
        setCookie('token', token, { days: 7 }); // Auth tokens are essential, so we can set them
      } else {
        // Store temporarily in sessionStorage until consent is given
        sessionStorage.setItem('pending_token', token);
        sessionStorage.setItem('pending_user', JSON.stringify(newUser));
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(newUser);
      return { success: true };
    } catch (error) {
      // Clear any partial authentication state on error
      logout();
      return { success: false, error: error.response?.data?.error || 'Registration failed' };
    }
  };

  const logout = () => {
    // Clear all stored authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('pending_token');
    sessionStorage.removeItem('pending_user');

    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const adoptSession = (token, userData) => {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(userData));
    localStorage.removeItem('rememberMe');
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const value = {
    user,
    loading,
    login,
    completeMfaLogin,
    completeDuoLogin,
    register,
    logout,
    adoptSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
